import React, { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useWalletContext } from '../../context/WalletContext';
import { fetchGatewayRewards } from '../../services/RewardsService';
import { ZapIcon } from '../icons/icons';
import { useTheme } from '../../context/ThemeContext';

interface WalletRewardsCardProps {
  isDragging?: boolean;
}

const WalletRewardsCard: React.FC<WalletRewardsCardProps> = ({ isDragging = false }) => {
  const [totalRewards, setTotalRewards] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { publicKey } = useWalletContext();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  useEffect(() => {
    // Skip if no wallet is connected
    if (!publicKey) {
      setTotalRewards(null);
      setError(null);
      return;
    }

    const fetchWalletRewards = async () => {
      setIsLoading(true);
      try {
        const walletAddress = publicKey.toString();
        
        // Use the gwId parameter as an empty string to get all rewards for this wallet
        const data = await fetchGatewayRewards('', walletAddress);
        
        if (data?.betaInfo?.total !== undefined) {
          // Always set the total rewards from betaInfo.total, even if it's 0
          setTotalRewards(data.betaInfo.total);
          setError(null);
        } else if (data?.betaDaily && data.betaDaily.length > 0) {
          // If betaInfo.total is undefined but we have betaDaily data,
          // calculate the total from daily rewards
          let calculatedTotal = 0;
          data.betaDaily.forEach(gateway => {
            if (gateway.rewards && gateway.rewards.length > 0) {
              gateway.rewards.forEach(reward => {
                if (reward.wallet === walletAddress) {
                  calculatedTotal += reward.amount;
                }
              });
            }
          });
          setTotalRewards(calculatedTotal);
          setError(null);
        } else {
          setTotalRewards(0);
          setError('No rewards data available');
        }
      } catch (err) {
        console.error('Error fetching wallet rewards:', err);
        setError('Failed to load rewards');
        setTotalRewards(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWalletRewards();
  }, [publicKey]);

  const formatTotal = (total: number) => {
    return total.toLocaleString();
  };

  return (
    <Link to="/rewards">
      <div className="w-full aspect-[2/1] sm:aspect-square">
        <div className={`block p-4 bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#292929] rounded-[8px] shadow hover:shadow-[0_0_10px_rgba(0,255,132,0.3)] hover:border-[#00FF84] transition-all duration-300 h-full w-full ${isLoading ? (isLightMode ? 'shimmer-effect light' : 'shimmer-effect dark') : ''}`}>
          <div className="flex flex-col h-full justify-center items-center">
            <div className="w-14 h-14 mb-5">
              {isLoading ? (
                <div className={`w-14 h-14 rounded-full ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              ) : (
                <ZapIcon className="w-full h-full" />
              )}
            </div>
            <h3 className="text-gray-800 dark:text-white text-base font-medium text-center">
              Your Rewards
            </h3>
            {isLoading ? (
              <div className={`w-20 h-5 rounded mt-2 ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
            ) : error ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center">
                {!publicKey ? 'Connect wallet to view' : error}
              </p>
            ) : (
              <p className="text-green-600 dark:text-green-400 text-xl font-bold text-center font-poly-sans-median">
                {formatTotal(totalRewards || 0)} Points
              </p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default WalletRewardsCard; 